import React from 'react';
import { CheckCircle2Icon, ShieldCheckIcon } from 'lucide-react';
import { ContactField, useContactForm } from '../hooks/useContactForm';
type FieldProps = {
  id: ContactField;
  label: string;
  type?: string;
  optional?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  value: string;
  error?: string;
  onChange: (field: ContactField, value: string) => void;
};
const controlBase = 'mt-1.5 block w-full rounded-md border bg-paper px-3.5 text-[16px] text-ink transition-colors duration-150 ease-out focus:border-ink';
function Field({
  id,
  label,
  type = 'text',
  optional,
  autoComplete,
  inputMode,
  value,
  error,
  onChange
}: FieldProps) {
  // Input ids are "contact-<field>"; "contact-name" is the focus target for every #contact link.
  const inputId = `contact-${id}`;
  const errorId = `${inputId}-error`;
  return <div className="min-w-0" data-id="element-73" data-mp="2i539z2cbjibj">
      <label htmlFor={inputId} className="block whitespace-nowrap text-[14px] font-bold leading-5 text-ink" data-id="element-74" data-mp="2e6dw78ndbyrd">
        {label}
        {optional && <span className="font-medium text-muted" data-id="element-75" data-mp="1clf7h4tuoycl"> (optional)</span>}
      </label>
      <input id={inputId} name={id} type={type} autoComplete={autoComplete} inputMode={inputMode} required={!optional} value={value} onChange={e => onChange(id, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} className={`${controlBase} h-11 ${error ? 'border-danger' : 'border-mist-line'}`} data-id="element-76" data-mp="1hjqkfoffzoz1" />
      {error && <p id={errorId} className="mt-1 text-[13px] font-semibold text-danger" data-id="element-77" data-mp="13la373jyeg2n">
          {error}
        </p>}
    </div>;
}
export function HeroContactForm() {
  const {
    values,
    errors,
    status,
    setField,
    handleSubmit,
    formRef
  } = useContactForm();
  return (
    // Design preview form: no action attribute and no network requests. See hooks/useContactForm
    // for notes on connecting the existing native form 5477207 / frame 7662611 in production.
    <form ref={formRef} noValidate onSubmit={handleSubmit} aria-labelledby="contact-form-title" className="rounded-lg border border-white/10 bg-paper p-5 md:p-6" data-id="element-78" data-mp="jvpnsmmrsndq">
      <h2 id="contact-form-title" className="text-[22px] font-extrabold leading-tight text-ink" data-id="element-79" data-mp="sxfefq8tviag">
        Start your project.
      </h2>
      <p className="mt-1 text-[15px] leading-[1.5] text-muted" data-id="element-80" data-mp="16sscffmzotx">
        Tell us what you want to build, improve, or connect.
      </p>

      <div className="mt-4 grid gap-3.5 sm:grid-cols-2" data-id="element-81" data-mp="3se59wvkfm4ad">
        <Field id="name" label="Name" autoComplete="name" value={values.name} error={errors.name} onChange={setField} data-id="element-82" data-mp="9z30lr6z1k8j" />
        <Field id="email" label="Email" type="email" autoComplete="email" value={values.email} error={errors.email} onChange={setField} data-id="element-83" data-mp="57usqzlai6xh" />
      </div>
      <div className="mt-3.5" data-id="element-84" data-mp="39lt8cb1lr71a">
        <Field id="phone" label="Phone" type="tel" optional autoComplete="tel" inputMode="tel" value={values.phone} error={errors.phone} onChange={setField} data-id="element-85" data-mp="39ki3s90c1v94" />
      </div>

      <div className="mt-3.5" data-id="element-86" data-mp="2plsyj0t4d1bd">
        <label htmlFor="contact-message" className="block text-[14px] font-bold leading-5 text-ink" data-id="element-87" data-mp="2pidle3j0a2cl">
          How can we help?
        </label>
        <textarea id="contact-message" name="message" rows={2} required value={values.message} onChange={e => setField('message', e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error' : undefined} className={`${controlBase} h-[72px] resize-y py-2.5 leading-6 ${errors.message ? 'border-danger' : 'border-mist-line'}`} data-id="element-88" data-mp="3i8vv3ne8j1yz" />
        {errors.message && <p id="contact-message-error" className="mt-1 text-[13px] font-semibold text-danger" data-id="element-89" data-mp="21vymglgmjgs6">
            {errors.message}
          </p>}
      </div>

      {/*
        CAPTCHA PREVIEW — visual placeholder only. It is intentionally disabled, never "solves",
        loads no external script or keys, and does not block the preview-only validation.
        No CAPTCHA credentials exist for the Magic Patterns preview host.
        PRODUCTION: use the existing native WebsiteForge form (ID 5477207 / frame 7662611) with its
        real CAPTCHA and server-side validation. Do not recreate or overwrite that production form.
       */}
      <div className="mt-4 flex min-h-[60px] items-center justify-between gap-3 rounded-md border border-mist-line bg-mist px-3.5 py-2.5" data-id="element-90" data-mp="2vzqe2rsn9dqb">
        <label htmlFor="contact-captcha-preview" className="flex min-w-0 items-center gap-3 text-[15px] font-semibold text-ink" data-id="element-91" data-mp="2m45barfr49fi">
          <input id="contact-captcha-preview" type="checkbox" disabled aria-describedby="contact-captcha-note" className="h-6 w-6 shrink-0 cursor-not-allowed rounded-[3px] border-2 border-muted/60 bg-paper" data-id="element-92" data-mp="1foct41j0r5vb" />
          I&rsquo;m not a robot
        </label>
        <div className="flex shrink-0 flex-col items-center text-center" data-id="element-93" data-mp="3r8u3tckohj4d">
          <ShieldCheckIcon className="h-5 w-5 text-muted" aria-hidden data-id="element-94" data-mp="310khhzb4n2" />
          <span id="contact-captcha-note" className="mt-0.5 text-[12px] font-semibold leading-tight text-muted" data-id="element-95" data-mp="4vlpp5pxwtk9">
            CAPTCHA preview
          </span>
        </div>
      </div>

      <button type="submit" className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center whitespace-nowrap rounded-md bg-brand-orange px-6 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:bg-brand-orangeHover" data-id="element-96" data-mp="dvqq2vwpurox">
        Let&rsquo;s get started
      </button>
      <div role="status" aria-live="polite" className="mt-2.5 text-center text-[13px] font-medium leading-snug text-muted" data-id="element-97" data-mp="2knhycppcblco">
        {status === 'checked' ? <span className="inline-flex items-center gap-1.5 font-semibold text-ink" data-id="element-98" data-mp="2hkvftca52mu5">
            <CheckCircle2Icon className="h-4 w-4 shrink-0 text-brand-greenDeep" aria-hidden data-id="element-99" data-mp="2hmolopcxh313" />
            Preview checked. No message was sent.
          </span> : 'Design preview. Form not connected.'}
      </div>
    </form>
  );
}